import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { BrandKit } from '../types'

// ─── Default Brand Kit ──────────────────────────────────────────────────────

const defaultBrandKit: BrandKit = {
  businessName: '',
  industry: '',
  description: '',
  targetAudience: '',
  toneOfVoice: ['friendly'],
  colors: {
    primary: '#6161ff',
    secondary: '#f5f5f5',
    accent: '#ff6b6b',
  },
  logoId: null,
  instagramProfilePictureId: null,
  fonts: {
    heading: 'Montserrat',
    body: 'Montserrat',
  },
  keywords: [],
  channels: {},
  goals: [],
  websiteUrl: null,
  images: [],
  contentFocus: null,
  timezone: null,
}

// ─── Persist helpers ────────────────────────────────────────────────────────

const BRAND_KIT_STORAGE_KEY = 'taka-creative-brand-kit'

// ─── Store ──────────────────────────────────────────────────────────────────

interface BrandKitStore {
  brandKit: BrandKit
  updateBrandKit: (updates: Partial<BrandKit>) => void
  setBrandKitFromApi: (data: BrandKit) => void
  resetBrandKit: () => void
}

/** Selector: returns logoId, falling back to instagramProfilePictureId */
export const selectBrandLogo = (s: BrandKitStore) =>
  s.brandKit.logoId || s.brandKit.instagramProfilePictureId

export const useBrandKitStore = create<BrandKitStore>()(
  persist(
    set => ({
      brandKit: defaultBrandKit,

      updateBrandKit: (updates: Partial<BrandKit>) => {
        set(state => ({
          brandKit: { ...state.brandKit, ...updates },
        }))
      },

      setBrandKitFromApi: (data: BrandKit) => {
        set(state => ({
          brandKit: { ...defaultBrandKit, ...state.brandKit, ...data },
        }))
      },

      resetBrandKit: () => {
        set({ brandKit: defaultBrandKit })
      },
    }),
    {
      name: BRAND_KIT_STORAGE_KEY,
      partialize: (state): { brandKit: BrandKit } => ({ brandKit: state.brandKit }),
      merge: (persisted, current) => {
        const raw = persisted as { brandKit?: BrandKit } | undefined
        if (!raw?.brandKit) return current
        return {
          ...current,
          brandKit: { ...defaultBrandKit, ...raw.brandKit },
        }
      },
    }
  )
)
